import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileContainer, mutationErrorHandler } from "am-tax-fe-core";

import { TOAST_DURATION } from "../../utils/constants";

import {
    getJurisdictionAsync,
    getJurisdictionsAsync,
    createJurisdictionAsync,
    updateJurisdictionAsync,
    deleteJurisdictionAsync,
    CreateOrUpdateJurisdiction,
} from "./jurisdictionApi";
import { queryKeys } from "../queryKeys";

export function useGetJurisdictions() {
    return useQuery({ ...queryKeys.jurisdictions.all, queryFn: getJurisdictionsAsync });
}

export const useGetJurisdiction = (id: string | undefined) => {
    const toast = useToast();
    return useQuery({
        ...queryKeys.jurisdictions.byId(id || ""),
        queryFn: async () => {
            const jurisdiction = await getJurisdictionAsync(id || "")
                .then(jurisdiction => jurisdiction)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the jurisdiction information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return jurisdiction;
        },
        enabled: !!id,
    });
};

export function useCreateJurisdiction() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { file: FileContainer | undefined; jurisdiction: CreateOrUpdateJurisdiction }) => {
            return await createJurisdictionAsync(args.jurisdiction, args.file);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.jurisdictions.all);
            toast({
                title: "Created",
                description: "Jurisdiction created successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to create jurisdiction"),
    });
}

export function useDeleteJurisdiction() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: string) => {
            return await deleteJurisdictionAsync(args);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.jurisdictions.all);
            toast({
                title: "Deleted",
                description: "Jurisdiction deleted successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to delete jurisdiction"),
    });
}

export function useUpdateJurisdiction() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { file: FileContainer | undefined; jurisdiction: CreateOrUpdateJurisdiction }) => {
            return await updateJurisdictionAsync(args.jurisdiction, args.file);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.jurisdictions.all);
            toast({
                title: "Updated",
                description: "Jurisdiction updated successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to update jurisdiction."),
    });
}
